import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';
import PlinthionLogo from '../../components/PlinthionLogo';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      {/* Mobile Hero Section */}
      <div className="lg:hidden" style={{backgroundColor: '#16207B'}}>
        <div className="px-6 py-8 text-center">
          <div className="flex justify-center mb-3">
            <PlinthionLogo size="large" light={true} />
          </div>
          <p className="text-sm text-primary-foreground/90 leading-relaxed max-w-md mx-auto">
            B2B Marketplace connecting Logistics & Warehousing Providers with Businesses needing Warehouse Solutions, Material Handling Equipment (MHE), Fleet & Manpower
          </p>
        </div>
      </div>

      <div className="flex min-h-[calc(100vh-180px)] lg:min-h-screen">
        {/* Desktop Hero Section */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12" style={{backgroundColor: '#16207B'}}>
          <div className="max-w-lg">
            <div className="mb-4">
              <PlinthionLogo size="xlarge" light={true} />
            </div>
            <p className="text-xl text-primary-foreground/90 leading-relaxed">
              B2B Marketplace connecting Logistics & Warehousing Providers with Businesses needing Warehouse Solutions, Material Handling Equipment (MHE), Fleet & Manpower
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-primary-foreground/80">Real-Time Space Availability</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-primary-foreground/80">Dynamic Pricing Engine</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-primary-foreground/80">Integrated Fleet, MHE & Manpower Solutions</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-8">
          <Card data-testid="login-card" className="w-full max-w-md p-6 lg:p-8 bg-white border border-border shadow-sm rounded-sm">
          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-2 tracking-tight">Sign In</h2>
            <p className="text-muted-foreground">Step Into Your Logistics Gateway</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm border-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm border-input"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              style={{backgroundColor: '#16207B'}}
              className="w-full text-primary-foreground hover:opacity-90 rounded-sm font-medium transition-all active:scale-95"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't Have An Account ?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Register here
              </Link>
            </p>
          </div>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default LoginPage;